import { reactive, h } from 'vue';
import { defineStore } from 'pinia';
import { IRegisterSettingUiState } from './interfaces/RegisterSettingUiState';
import { FileUploadOptions, useDropzone } from 'vue3-dropzone';
import { columnToNumber, getExcelTypeFromFileName } from '@/appLogic/fileManager/translate/excelUtils';
import { i18n } from '@/i18n';
import { assertNever } from '@/coreDomainsModule/assertNever';
import { AvailableUploadFileName, SelectYearMonthPattern, UploadInputFileType, UpsertBusinessError } from './interfaces/UploadFile';
import { ReadFileService } from '../service/ReadFileService';
import { LINKING_BUSINESS_HEADERS, LinkingBusinessHeader } from './interfaces/LinkingBusiness';
import { RegisterExcelBusinessInfoUseCase } from '../useCases/registerExcelBusinessInfoUseCase';
import { BusinessExcelInfosRepository } from '@/coreInfrastructureModule/Repositories/BusinessExcelInfosRepository';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { UpsertBusinessesFromExcelUseCase } from '../useCases/UpsertBusinessesFromExcelUseCase';
import { UpsertBusinessesRepository } from '@/coreInfrastructureModule/Repositories/UpsertBusinessesRepository';
import { openLoadingModal, openOkInformationModal, openSuccessToSaveModal } from '@/corePagesModule/components/modal/modalActions';
import { CreateCsvUseCase } from '@/exportFileUseCasesModule/createCsvUseCase';
import {
  AlterableHeaderInfo,
  AlterableHeaderService,
  InputFileType,
  isRequireAlterableHeader,
  SelectYearMonthPatternInHeader,
  SelectYearMonthPatternInlined,
} from './interfaces/LinkingInput';
import { RegisterExcelInputInfoUseCase } from '../useCases/RegisterExcelInputInfoUseCase';
import { PutExcelInputInfoAdapter } from '../adapters/PutExcelInputInfoAdapter';
import { InputExcelInfosRepository } from '@/coreInfrastructureModule/Repositories/InputExcelInfosRepository';
import { GetExcelInputInfoUseCase } from '../useCases/GetExcelInputInfoUseCase';
import { InputNumberInput } from './interfaces/InputField';
import { RegisterExcelInfo } from './interfaces/HomeInput';
import { GetExcelBusinessInfoUseCase } from '../useCases/GetExcelBusinessInfoUseCase';
import { BusinessExcelInfo } from '@/coreDomainsModule/entities/businessExcelInfo';
import { diContainer } from '@/coreContainer/container';

/**
 * RegisterSettingPageStore（引数でDIする）
 * @param getExcelInputInfoUseCase - 入力読み替え情報取得UseCase
 * @param getExcelBusinessInfoUseCase - 取引読み替え情報取得UseCase
 * @param registerExcelBusinessInfoUseCase - 取引読み替え情報登録UseCase
 * @param businessExcelInfosRepository - 取引読み替え情報リポジトリ
 * @param upsertBusinessesFromExcelUseCase - 取引一括登録UseCase
 * @param createCsvUseCase - CSV作成UseCase
 * @param registerExcelInputInfoUseCase - 入力読み替え情報登録UseCase
 * @param upsertBusinessesRepository - 取引一括登録リポジトリ
 * @param inputExcelInfosRepository - 入力読み替え情報リポジトリ
 * @param putExcelInputInfoAdapter - 入力読み替え情報アダプタ
 */
export const useRegisterSettingStore = (
  getExcelInputInfoUseCase: GetExcelInputInfoUseCase,
  getExcelBusinessInfoUseCase: GetExcelBusinessInfoUseCase,
  registerExcelBusinessInfoUseCase: RegisterExcelBusinessInfoUseCase,
  businessExcelInfosRepository: BusinessExcelInfosRepository,
  upsertBusinessesFromExcelUseCase: UpsertBusinessesFromExcelUseCase,
  createCsvUseCase: CreateCsvUseCase,
  registerExcelInputInfoUseCase: RegisterExcelInputInfoUseCase,
  upsertBusinessesRepository: UpsertBusinessesRepository,
  inputExcelInfosRepository: InputExcelInfosRepository,
  putExcelInputInfoAdapter: PutExcelInputInfoAdapter
) =>
  defineStore('RegisterSettingPage', () => {
    const state = reactive<IRegisterSettingUiState>({
      mode: 'FirstRegister',
      uploadBusiness: {
        inputFile: undefined,
        businessInputHeaderNumber: {
          value: undefined,
          validateError: 'None',
        },
        businessInputData: undefined,
        businessHeaderCandidate: [],
      },
      homeInput: {
        isEdited: {
          Inventory: false,
          SalesResults: false,
          Arrival: false,
        },
        inputExcelInfo: undefined,
      },
      uploadInput: {
        inputFileType: '在庫', // 初期値はnullが適切だが、とりあえず在庫で
        inputFile: undefined,
        useFormatType: 'Inlined',
        headerNumber: {
          value: undefined,
          validateError: 'None',
        },
        data: undefined,
        headerCandidates: [],
      },
      linkingBusiness: {
        useLinkingType: 'Original',
        linkedBusinessHeaders: LINKING_BUSINESS_HEADERS,
      },
      linkingInput: {
        alternateHeaderInfos: [],
        inlinedYearMonthInfo: {
          headerName: '',
          validateError: 'none',
        },
        inHeaderYearMonthInfo: {
          startColumnNumber: {
            raw: '',
            validateError: 'none',
          },
          endColumnNumber: {
            raw: '',
            validateError: 'none',
          },
          start: {
            raw: '',
            validateError: 'none',
          },
          end: {
            raw: '',
            validateError: 'none',
          },
        },
      },
    });

    const t = i18n.t;

    /**
     * ドロップゾーンからファイルを読み込む
     * @param acceptFiles - 受け入れられたファイル
     * @param rejectReasons - 拒否された理由
     */
    function onDrop(acceptFiles, rejectReasons) {
      const acceptFile = acceptFiles[0];
      const acceptFileName = acceptFile?.name;

      /**
       * ファイル名をチェックする関数
       * @param fileName - ファイル名
       * @param fileType - ファイルタイプ
       */
      const _validFileName = (fileName: string, fileType: AvailableUploadFileName) => {
        const validFileName = fileType + '_';
        // 英語ファイル名対応処理
        switch (fileType) {
          case '在庫表':
            if (fileName.includes('MasterData_') || fileName.includes(validFileName)) return true;
            break;
          case '販売実績':
            if (fileName.includes('SalesResult_') || fileName.includes(validFileName)) return true;
            break;
          case '入荷':
            if (fileName.includes('Arrival_') || fileName.includes(validFileName)) return true;
            break;
          case '在庫':
            if (fileName.includes('Stock_') || fileName.includes(validFileName)) return true;
            break;
          default:
            assertNever(fileType);
            break;
        }
        return false;
      };

      // ファイル名が取得できなかった場合はアラートを出力する
      if (!acceptFileName) {
        alert(t('取引設定：アラート文言：ファイル名取得失敗'));
        return;
      }

      const fileType = acceptFileName.split('.').pop();

      // 有効な拡張子かどうか
      if (fileType !== 'xlsx') {
        alert(t('取引設定：アラート文言：ファイル形式'));
        return;
      }

      // acceptFilesが1つかどうか
      if (acceptFiles.length > 1) {
        alert(t('取引設定：アラート文言：複数ファイル読み込み'));
        return;
      }

      const excelType = getExcelTypeFromFileName(acceptFileName);
      switch (state.mode) {
        case 'UploadBusiness':
          if (excelType !== '在庫表') {
            alert(t('取引設定：アラート文言：在庫表アップロード'));
            return;
          }
          if (!_validFileName(acceptFileName, '在庫表')) {
            alert(t('取引設定：アラート文言：不正なファイル名'));
            return;
          }
          state.uploadBusiness.inputFile = acceptFile; // 全部問題ない場合だけファイルをセットする
          break;
        case 'UploadInput':
          switch (state.uploadInput.inputFileType) {
            case '在庫':
              if (excelType !== '在庫') {
                alert(t('取引設定：アラート文言：在庫ファイルアップロード'));
                return;
              }
              if (!_validFileName(acceptFileName, '在庫')) {
                alert(t('取引設定：アラート文言：不正なファイル名'));
                return;
              }
              break;
            case '販売実績':
              if (excelType !== '販売実績') {
                alert(t('取引設定：アラート文言：販売実績ファイルアップロード'));
                return;
              }
              if (!_validFileName(acceptFileName, '販売実績')) {
                alert(t('取引設定：アラート文言：不正なファイル名'));
                return;
              }
              break;
            case '入荷':
              if (excelType !== '入荷') {
                alert(t('取引設定：アラート文言：入荷ファイルアップロード'));
                return;
              }
              if (!_validFileName(acceptFileName, '入荷')) {
                alert(t('取引設定：アラート文言：不正なファイル名'));
                return;
              }
              break;
            default:
              assertNever(state.uploadInput.inputFileType);
              break;
          }
          state.uploadInput.inputFile = acceptFile; // 全部問題ない場合だけファイルをセットする
          break;
        case 'FirstRegister':
        case 'HomeInput':
        case 'LinkingBusiness':
        case 'LinkingInput':
        case 'FinishBusiness':
        case 'FinishInput':
          break;
        default:
          assertNever(state.mode);
          break;
      }
    }

    /**
     * ファイルを削除する
     * @param _ - いらない
     */
    const handleClickDeleteFile = function (_) {
      state.uploadBusiness.inputFile = undefined;
    };

    const dropzoneOptions2: Partial<FileUploadOptions> = {
      onDrop: onDrop,
    };
    const { getRootProps, getInputProps, isDragActive, ...rest } = useDropzone(dropzoneOptions2);

    /**
     * stateを初期化する
     */
    const $reset = () => {
      state.mode = 'FirstRegister';
      state.uploadBusiness.inputFile = undefined;
      state.uploadBusiness.businessInputHeaderNumber = {
        value: undefined,
        validateError: 'None',
      };
      state.uploadBusiness.businessInputData = undefined;
      state.uploadBusiness.businessHeaderCandidate = [];

      state.homeInput.isEdited = {
        Inventory: false,
        SalesResults: false,
        Arrival: false,
      };
      state.homeInput.inputExcelInfo = undefined;

      state.uploadInput.inputFileType = '在庫';
      state.uploadInput.inputFile = undefined;
      state.uploadInput.useFormatType = 'Inlined';
      state.uploadInput.headerNumber = {
        value: undefined,
        validateError: 'None',
      };
      state.uploadInput.data = undefined;
      state.uploadInput.headerCandidates = [];

      state.linkingBusiness.useLinkingType = 'Original';
      state.linkingBusiness.linkedBusinessHeaders = LINKING_BUSINESS_HEADERS;
      state.linkingBusiness.previousExcelInfo = undefined;

      state.linkingInput.alternateHeaderInfos = [];
      state.linkingInput.inlinedYearMonthInfo = {
        headerName: '',
        validateError: 'none',
      };
      state.linkingInput.inHeaderYearMonthInfo = {
        startColumnNumber: {
          raw: '',
          validateError: 'none',
        },
        endColumnNumber: {
          raw: '',
          validateError: 'none',
        },
        start: {
          raw: '',
          validateError: 'none',
        },
        end: {
          raw: '',
          validateError: 'none',
        },
      };
    };

    /**
     * データを取得する
     */
    const fetchInputHomeData = async () => {
      const res = await getExcelInputInfoUseCase.Execute(inputExcelInfosRepository); // NOTE: 仕組み上絶対に取得できるのでハンドリングしてない
      if (res.status.type === 'success') {
        state.homeInput.inputExcelInfo = res.status.data;
      }
    };

    /**
     * 初回登録の次へボタン
     * @param isSelectedRegisterBusiness - 取引情報登録が選択されているか
     */
    const onClickFirstPageNext = (isSelectedRegisterBusiness: boolean) => {
      $reset(); // 一旦リセット
      state.mode = isSelectedRegisterBusiness ? 'UploadBusiness' : 'HomeInput';
    };

    /**
     * 取引アップロードの戻るボタン
     */
    const onClickUploadBusinessBack = () => {
      state.mode = 'FirstRegister';
    };

    /**
     * 入力ホームの戻るボタン
     */
    const onClickHomeInputBack = () => {
      // NOTE: 全消しして最初の画面に戻す
      $reset();
    };

    /**
     * 入力アップロードの戻るボタン
     */
    const onClickUploadInputBack = () => {
      state.mode = 'HomeInput';
    };

    /**
     * 取引連携の戻るボタン
     */
    const onClickLinkingBusinessBack = () => {
      state.mode = 'UploadBusiness';
    };

    /**
     * 入力連携の戻るボタン
     */
    const onClickLinkingInputBack = () => {
      state.mode = 'UploadInput';
    };

    /**
     * 取引連携完了画面の戻るボタン
     */
    const onClickFinishBusinessBack = () => {
      // NOTE: 全消しして最初の画面に戻す
      $reset();
    };

    /**
     * 入力連携完了画面の戻るボタン
     */
    const onClickFinishInputBack = () => {
      // NOTE: 全消しして最初の画面に戻す
      $reset();
      state.mode = 'HomeInput';
    };

    /**
     * 取引アップロードの次へボタン
     * 入力ファイルからヘッダーの候補を取得してLinkingBusinessに遷移する
     */
    const onClickUploadBusinessNext = async () => {
      const uploadedFile = state?.uploadBusiness.inputFile as unknown as File;

      if (!uploadedFile) {
        alert(t('取引設定：アラート文言：在庫表アップロード'));
        return;
      } // ありえないけど一応

      // ヘッダー番号のバリデーション
      const headerNumber = state.uploadBusiness.businessInputHeaderNumber.value;
      if (!headerNumber) {
        state.uploadBusiness.businessInputHeaderNumber.validateError = 'Empty';
        return;
      } else if (headerNumber < 1) {
        state.uploadBusiness.businessInputHeaderNumber.validateError = 'AtLeastOne';
        return;
      }

      [state.uploadBusiness.businessHeaderCandidate, state.uploadBusiness.businessInputData] = await ReadFileService.readHeaderCandidate(
        uploadedFile,
        headerNumber
      );

      const res = await getExcelBusinessInfoUseCase.Execute(businessExcelInfosRepository);
      if (res.status.type === 'success') {
        // NOTE: 仕組み上、取得成功（データが存在）した場合にしかこの分岐に来ない
        state.linkingBusiness.previousExcelInfo = {
          headerLineNumber: res.status.data.headerLineNumber,
          alternateHeaderInfos: res.status.data.alternateHeaderInfos,
        };
      }

      state.mode = 'LinkingBusiness';
    };

    /**
     * 入力アップロードの次へボタン
     * 入力ファイルからヘッダーの候補を取得してLinkingInputに遷移する
     */
    const onClickUploadInputNext = async () => {
      const uploadedFile = state?.uploadInput.inputFile as unknown as File;

      if (!uploadedFile) {
        switch (state.uploadInput.inputFileType) {
          case '在庫':
            alert(t('取引設定：アラート文言：在庫ファイルアップロード'));
            return;
          case '販売実績':
            alert(t('取引設定：アラート文言：販売実績ファイルアップロード'));
            return;
          case '入荷':
            alert(t('取引設定：アラート文言：入荷ファイルアップロード'));
            return;
          default:
            assertNever(state.uploadInput.inputFileType);
            break;
        }
        return;
      } // ありえないけど一応

      // ヘッダー番号のバリデーション
      const headerNumber = state.uploadInput.headerNumber.value;
      if (!headerNumber) {
        state.uploadInput.headerNumber.validateError = 'Empty';
        return;
      } else if (headerNumber < 1) {
        state.uploadInput.headerNumber.validateError = 'AtLeastOne';
        return;
      }

      [state.uploadInput.headerCandidates, state.uploadInput.data] = await ReadFileService.readHeaderCandidate(uploadedFile, headerNumber);

      // 初期化はここで行う
      if (state.linkingInput.alternateHeaderInfos.length === 0) {
        switch (state.uploadInput.inputFileType) {
          case '在庫':
            state.linkingInput.alternateHeaderInfos = AlterableHeaderService.inventoryAlterableHeaderList.map((header) => {
              return {
                header: header,
                value: '',
                unused: false,
                validateError: false,
              };
            });
            break;
          case '販売実績':
            state.linkingInput.alternateHeaderInfos = AlterableHeaderService.salesResultsAlterableHeaderList.map((header) => {
              return {
                header: header,
                value: '',
                unused: false,
                validateError: false,
              };
            });
            break;
          case '入荷':
            state.linkingInput.alternateHeaderInfos = AlterableHeaderService.arrivalAlterableHeaderList.map((header) => {
              return {
                header: header,
                value: '',
                unused: false,
                validateError: false,
              };
            });
            break;
          default:
            assertNever(state.uploadInput.inputFileType);
            break;
        }
      }
      state.mode = 'LinkingInput';
    };

    /**
     * 取引連携の次へボタン
     * 紐付けされた情報を読み替え情報として保存、必要であれば取引一括登録を行い、FinishBusinessに遷移する
     */
    const onClickLinkingBusinessNext = async () => {
      /**
       * バリデーション
       */
      const _validate = () => {
        let isValid = true;
        const linkedBusinessHeaders = state.linkingBusiness.linkedBusinessHeaders;
        for (const linkedBusinessHeader of linkedBusinessHeaders) {
          // 未選択チェック
          if (linkedBusinessHeader.required && !linkedBusinessHeader.value && !linkedBusinessHeader.unused) {
            linkedBusinessHeader.validateError = 'NotSelected';
            isValid = false;
          } else {
            linkedBusinessHeader.validateError = 'None';
          }
        }
        return isValid;
      };

      /**
       * 読み替え情報に変換する
       * @param linkingBusinessHeader - 紐付け情報
       */
      const _convertRegisterBusinessInfo = (linkingBusinessHeader: LinkingBusinessHeader[]): BusinessExcelInfo => {
        const output: BusinessExcelInfo = {
          headerLineNumber: state.uploadBusiness.businessInputHeaderNumber?.value ?? 0, // nullはありえない
          alternateHeaderInfos: linkingBusinessHeader.map((header) => {
            return {
              header: header.key,
              value: header.value ?? '', // nullはありえない
              unused: header.unused === true,
            };
          }),
        };
        return output;
      };

      /**
       * ローディングモーダルを表示し、閉じるためのハンドラを返す
       * @param title - モーダルタイトル
       * @param description - モーダル説明
       * @returns モーダルを閉じる非同期関数
       */
      const startBlockingLoadingModal = (title: string, description: string): (() => Promise<void>) => {
        let closeHandler: (() => void) | undefined;
        let isClosed = false;
        const modalPromise = showInfoModal({
          modalTitle: title,
          modalDescription: description,
          icon: 'loading',
          enableOutsideClick: false,
          backButtonLabel: '',
          nextButtonLabel: '',
          isDisabledScroll: true,
          /**
           * ローディングモーダルを閉じるためのハンドラ
           * @param helpers - InfoModalの制御ヘルパー
           */
          onInitialized: (helpers) => {
            /**
             *
             */
            closeHandler = () => helpers.onNext();
          },
        });
        return async () => {
          if (isClosed) return;
          isClosed = true;
          closeHandler?.();
          await modalPromise;
        };
      };

      if (!_validate()) {
        return;
      }

      const closeLoadingModal = startBlockingLoadingModal(
        t('取引紐付け：データ取り込みモーダル：タイトル'),
        t('取引紐付け：データ取り込みモーダル：本文')
      );

      // 読み替え情報の登録（失敗しないはずなのでハンドリングしない）
      // TODO: 余裕あったらhttps://www.figma.com/design/73OPKV1XKtvHeJjCf0sJA7/%E3%83%AA%E3%83%BC%E3%83%B3%E3%83%91%E3%82%B9-%2F%E6%9C%88%E6%AC%A1?node-id=18865-76122&t=ElFSHvzfzveQV58f-4
      // progressで管理できるようにしたい
      await registerExcelBusinessInfoUseCase.Execute(
        '新規登録',
        '新規登録', // NOTE: 複数対応できるが、とりあえず1つだけにしている
        _convertRegisterBusinessInfo(state.linkingBusiness.linkedBusinessHeaders),
        businessExcelInfosRepository
      );

      // 取引一括登録前: ユーザー存在チェック（事前検証）
      // 無効メールが含まれる行は登録APIから除外し、登録後のエラーCSVに記録する
      const preValidateErrors: UpsertBusinessError[] = [];
      let filteredBusinessInputData = state.uploadBusiness.businessInputData ?? [];
      let linked: Record<string, string | undefined> = {};
      try {
        linked = state.linkingBusiness.linkedBusinessHeaders.reduce<Record<string, string | undefined>>((acc, cur) => {
          acc[cur.key] = cur.value;
          return acc;
        }, {} as Record<string, string | undefined>);
        const aggRespHeader = linked['aggregateBusinessResponsibleUsers'];
        const baseRespHeader = linked['baseBusinessResponsibleUsers'];

        /**
         * カンマ区切り文字列を分割・トリムし、空要素を除外して配列で返す。
         * @param raw - 入力値（カンマ区切り想定）
         * @returns 正規化されたメールアドレス配列
         */
        const splitIds = (raw: unknown): string[] =>
          String(raw ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

        const emails = new Set<string>();
        (state.uploadBusiness.businessInputData ?? []).forEach((row) => {
          if (aggRespHeader) splitIds(row?.[aggRespHeader]?.value).forEach((m) => emails.add(m));
          if (baseRespHeader) splitIds(row?.[baseRespHeader]?.value).forEach((m) => emails.add(m));
        });

        let invalidEmailSet: Set<string> = new Set();
        if (emails.size > 0) {
          const verifyResult = await diContainer.verifyUserIdsUseCase.Execute(Array.from(emails));
          if (verifyResult.status.type === 'error') {
            await closeLoadingModal();
            await openOkInformationModal('error', '500', 'OK', verifyResult.status.error.message);
            return;
          }
          const notFound: string[] = verifyResult.status.data.notFoundUserIds ?? [];
          invalidEmailSet = new Set(notFound);

          if (invalidEmailSet.size > 0) {
            /**
             * カンマ区切り文字列内に指定メールが含まれるか判定する。
             * @param raw - 入力値（カンマ区切り想定）
             * @param mail - 判定対象のメールアドレス
             * @returns 含まれる場合は true
             */
            const includesMail = (raw: unknown, mail: string) =>
              String(raw ?? '')
                .split(',')
                .map((s) => s.trim())
                .includes(mail);

            (state.uploadBusiness.businessInputData ?? []).forEach((row) => {
              for (const mail of invalidEmailSet) {
                if (
                  (aggRespHeader && includesMail(row?.[aggRespHeader]?.value, mail)) ||
                  (baseRespHeader && includesMail(row?.[baseRespHeader]?.value, mail))
                ) {
                  preValidateErrors.push({
                    error: 'InvalidValue',
                    cellAddress: Object.values(row)?.[0]?.address?.toString() ?? '',
                    message: `メールが存在せず登録に失敗した: ${mail}`,
                  });
                }
              }
            });

            // 無効メールが含まれる行を除外
            /**
             * 行に無効メールが含まれるかを判定する。
             * @param row - Excelの1行
             * @returns 無効メールが含まれる場合は true
             */
            const rowHasInvalidMail = (row: any): boolean => {
              const agg = aggRespHeader ? splitIds(row?.[aggRespHeader]?.value) : [];
              const base = baseRespHeader ? splitIds(row?.[baseRespHeader]?.value) : [];
              return [...agg, ...base].some((m) => invalidEmailSet.has(m));
            };
            filteredBusinessInputData = (state.uploadBusiness.businessInputData ?? []).filter((row) => !rowHasInvalidMail(row));
          }
        }
      } catch (e) {
        await closeLoadingModal();
        await openOkInformationModal('error', '500', 'OK', 'ユーザー存在チェックに失敗しました');
        console.error(e);
        return;
      }

      // 事前チェックで除外した「行」件数を計算（同一行に複数メールがあっても1と数える）
      const invalidRowCount = Array.from(
        new Set(
          preValidateErrors.map((e) => (e.cellAddress ? e.cellAddress.toString().replace(/[^0-9]/g, '') : '')).filter((v) => v.length > 0)
        )
      ).length;

      /**
       * 必須キーが揃っている行か判定する。
       * 必須: warehouseNumber, package, businessNumber, supplierNumber
       * @param row - Excelの1行
       * @returns 必須キーが全て非空なら true
       */
      const hasRequiredKeys = (row: any): boolean => {
        const requiredKeys = ['warehouseNumber', 'package', 'businessNumber', 'supplierNumber'] as const;
        return requiredKeys.every((k) => {
          const header = linked[k];
          if (!header) return false;
          const v = row?.[header]?.value;
          return v != null && String(v).trim() !== '';
        });
      };

      const eligibleRowCount = filteredBusinessInputData.filter((row) => hasRequiredKeys(row)).length;

      // 取引一括登録（無効メールは除外済みデータで実行）
      const res = await upsertBusinessesFromExcelUseCase.Execute(
        filteredBusinessInputData,
        _convertRegisterBusinessInfo(state.linkingBusiness.linkedBusinessHeaders),
        upsertBusinessesRepository
      );

      await closeLoadingModal();

      switch (res.status.type) {
        case 'success':
          const apiValidateErrors: UpsertBusinessError[] = res.status.data.validateErrors;
          const mergedErrors: UpsertBusinessError[] = [...apiValidateErrors, ...preValidateErrors];
          const isInvalidated: boolean = mergedErrors.length > 0;
          const registerCount: number = eligibleRowCount + invalidRowCount; // 対象行 + 除外行（メール不備）
          const registeredAggregateBusinessCount: number = res.status.data.result.registeredBusinessCounts.aggregateBusiness;
          const registeredBusinessCount: number = res.status.data.result.registeredBusinessCounts.baseBusiness;

          const successHtml = isInvalidated
            ? t('取引紐付け：データ取り込みモーダル：成功バリデーションエラー有り：テキスト', {
                count: registerCount,
                successCount: registeredAggregateBusinessCount + registeredBusinessCount,
              })
            : t('取引紐付け：データ取り込みモーダル：成功バリデーションエラー無し：テキスト', {
                count: registerCount,
                successCount: registeredAggregateBusinessCount + registeredBusinessCount,
              });
          const successModalResult = await showInfoModal({
            modalTitle: t('取引紐付け：データ取り込みモーダル：成功：タイトル'),
            modalDescription: undefined,
            /**
             *
             */
            modalContent: () => h('div', { innerHTML: successHtml }),
            icon: 'info',
            colorStyle: isInvalidated ? 'error' : 'primary',
            enableOutsideClick: false,
            backButtonLabel: isInvalidated ? t('取引紐付け：データ取り込みモーダル：エラー時終了ボタン') : '',
            nextButtonLabel: isInvalidated ? '' : 'OK',
            isCenterContent: true,
          });

          if (!successModalResult && isInvalidated) {
            const output = ReadFileService.createUpsertBusinessesFromExcelErrorCsvData(mergedErrors);
            await createCsvUseCase.Execute(output, t('エラーCSV'));
          }
          break;
        case 'error':
          // APIエラー
          await openOkInformationModal('error', String(res.status.error.statusCode ?? 500), 'OK', res.status.error.message);
          return; // API起因の時は、何もしないで閉じる

        default:
          assertNever(res.status);
          break;
      }

      // 成功したら、次の画面に遷移
      state.mode = 'FinishBusiness';
    };

    /**
     * 取引連携の前の値を使うボタン
     * 前回の情報と読み込んだエクセルの値に合致するものを読み込む
     */
    const onClickLinkingBusinessUsePreviousValue = () => {
      const previousExcelInfo = state.linkingBusiness.previousExcelInfo;
      if (!previousExcelInfo) {
        return;
      }

      const linkedBusinessHeaders = state.linkingBusiness.linkedBusinessHeaders;

      for (const linkedBusinessHeader of linkedBusinessHeaders) {
        const previousAlternateHeaderInfo = previousExcelInfo.alternateHeaderInfos.find(
          (alternateHeaderInfo) => alternateHeaderInfo.header === linkedBusinessHeader.key
        );

        // 合致するヘッダー行 ^ suggestsの中に存在するヘッダー行
        if (
          previousAlternateHeaderInfo &&
          state.uploadBusiness.businessHeaderCandidate.some((header) => header.value === previousAlternateHeaderInfo.value)
        ) {
          linkedBusinessHeader.value = previousAlternateHeaderInfo.value;
        }
      }
    };

    /**
     * 入力連携の次へボタン
     * 紐付けされた情報を読み替え情報として保存、必要であれば取引一括登録を行い、FinishInputに遷移する
     */
    const onClickLinkingInputNext = async () => {
      const res = await _registerExcelInfoForm(
        state.uploadInput.inputFileType,
        state.uploadInput.headerNumber,
        state.uploadInput.useFormatType,
        state.linkingInput.alternateHeaderInfos,
        state.linkingInput.inlinedYearMonthInfo,
        state.linkingInput.inHeaderYearMonthInfo
      );

      if (res) {
        state.mode = 'FinishInput';
      }
    };

    /**
     * 取引連携の戻るボタン
     * @param inputFileType - 入力ファイルタイプ
     * @param headerNumber - ヘッダー番号
     * @param useFormatType - 年月情報の形式
     * @param alternateHeaderInfos - 読み替え情報
     * @param inlinedYearMonthInfo - 年月情報(inlined)
     * @param inHeaderYearMonthInfo - 年月情報(inHeader)
     */
    const _registerExcelInfoForm = async (
      inputFileType: UploadInputFileType,
      headerNumber: InputNumberInput,
      useFormatType: SelectYearMonthPattern,
      alternateHeaderInfos: AlterableHeaderInfo[],
      inlinedYearMonthInfo: SelectYearMonthPatternInlined,
      inHeaderYearMonthInfo: SelectYearMonthPatternInHeader
    ): Promise<boolean> => {
      /**
       * 登録情報のバリデーション(inputFileType単位で実行)
       * @param useFormatType - 年月情報の形式
       * @param alternateHeaderInfos - 読み替え情報
       * @param inlinedYearMonthInfo - 年月情報(inlined)
       * @param inHeaderYearMonthInfo - 年月情報(inHeader)
       * return: エラー数
       */
      const _validateRegisterExcelInfoForm = (
        useFormatType: SelectYearMonthPattern,
        alternateHeaderInfos: AlterableHeaderInfo[],
        inlinedYearMonthInfo: SelectYearMonthPatternInlined,
        inHeaderYearMonthInfo: SelectYearMonthPatternInHeader
      ): number => {
        let validateCount = 0;
        const regYearMonth = new RegExp('^\\d{6}$');
        const regExcelRow = new RegExp('^[A-Z]+$');

        // エラー情報を初期化
        alternateHeaderInfos.forEach((alternateHeaderInfo) => {
          alternateHeaderInfo.validateError = false;
        });
        inlinedYearMonthInfo.validateError = 'none';
        inHeaderYearMonthInfo.startColumnNumber.validateError = 'none';
        inHeaderYearMonthInfo.endColumnNumber.validateError = 'none';
        inHeaderYearMonthInfo.start.validateError = 'none';
        inHeaderYearMonthInfo.end.validateError = 'none';

        // 読み替え項目
        alternateHeaderInfos.forEach((alternateHeaderInfo) => {
          if (
            isRequireAlterableHeader(alternateHeaderInfo.header) &&
            alternateHeaderInfo.value === '' &&
            !(alternateHeaderInfo.unused === true)
          ) {
            alternateHeaderInfo.validateError = true; // 空白はエラー
            validateCount++;
          }
        });

        // 年月情報(inlined)
        if (useFormatType === 'Inlined') {
          if (inlinedYearMonthInfo.headerName === '') {
            inlinedYearMonthInfo.validateError = 'notEmpty';
            validateCount++;
          }
        }

        // 年月情報(inHeader)
        if (useFormatType === 'InHeader') {
          if (inHeaderYearMonthInfo.startColumnNumber.raw === '') {
            inHeaderYearMonthInfo.startColumnNumber.validateError = 'notEmpty';
            validateCount++;
          } else if (!regExcelRow.test(inHeaderYearMonthInfo.startColumnNumber.raw)) {
            inHeaderYearMonthInfo.startColumnNumber.validateError = 'cellPattern';
            validateCount++;
          }
          if (inHeaderYearMonthInfo.endColumnNumber.raw === '') {
            inHeaderYearMonthInfo.endColumnNumber.validateError = 'notEmpty';
          } else if (!regExcelRow.test(inHeaderYearMonthInfo.endColumnNumber.raw)) {
            inHeaderYearMonthInfo.endColumnNumber.validateError = 'cellPattern';
            validateCount++;
          }
          if (inHeaderYearMonthInfo.start.raw === '') {
            inHeaderYearMonthInfo.start.validateError = 'notEmpty';
            validateCount++;
          } else if (!regYearMonth.test(inHeaderYearMonthInfo.start.raw)) {
            inHeaderYearMonthInfo.start.validateError = 'yearMonth';
            validateCount++;
          }
          if (inHeaderYearMonthInfo.end.raw === '') {
            inHeaderYearMonthInfo.end.validateError = 'notEmpty';
            validateCount++;
          } else if (!regYearMonth.test(inHeaderYearMonthInfo.end.raw)) {
            inHeaderYearMonthInfo.end.validateError = 'yearMonth';
            validateCount++;
          }

          // 年月情報(inHeader) → セルが隣接しているかどうか
          if (
            inHeaderYearMonthInfo.startColumnNumber.validateError === 'none' &&
            inHeaderYearMonthInfo.endColumnNumber.validateError === 'none'
          ) {
            const startColumnNumber = columnToNumber(inHeaderYearMonthInfo.startColumnNumber.raw);
            const endColumnNumber = columnToNumber(inHeaderYearMonthInfo.endColumnNumber.raw);
            if (startColumnNumber > endColumnNumber) {
              // start = endの場合はエラーにしない（単月での登録を許可するため）
              inHeaderYearMonthInfo.startColumnNumber.validateError = 'sideBySideCell'; // startのエラーだけ発火させればいいや
              validateCount++;
            }
          }

          // 年月情報(inHeader) → 年月が隣接しているかどうか
          if (inHeaderYearMonthInfo.start.validateError === 'none' && inHeaderYearMonthInfo.end.validateError === 'none') {
            const startYear = Number(inHeaderYearMonthInfo.start.raw.slice(0, 4));
            const startMonth = Number(inHeaderYearMonthInfo.start.raw.slice(4));
            const endYear = Number(inHeaderYearMonthInfo.end.raw.slice(0, 4));
            const endMonth = Number(inHeaderYearMonthInfo.end.raw.slice(4));
            if (startYear > endYear || (startYear === endYear && startMonth > endMonth)) {
              // start = endの場合はエラーにしない（単月での登録を許可するため）
              inHeaderYearMonthInfo.start.validateError = 'sideBySideYearMonth'; // startのエラーだけ発火させればいいや
              validateCount++;
            }
          }
        }
        return validateCount;
      };

      const errCount = _validateRegisterExcelInfoForm(useFormatType, alternateHeaderInfos, inlinedYearMonthInfo, inHeaderYearMonthInfo); // バリデーション

      if (errCount > 0) {
        return false;
      }

      await openLoadingModal(t('登録中'), registerExcelInputInfoUseCase.Execute, [
        '新規登録',
        '新規登録',
        inputFileType,
        headerNumber,
        useFormatType,
        alternateHeaderInfos,
        inlinedYearMonthInfo,
        inHeaderYearMonthInfo,
        putExcelInputInfoAdapter,
        inputExcelInfosRepository,
      ]);
      openSuccessToSaveModal();

      return true;
    };

    /**
     * サンプルのクリックイベント
     */
    const retry = () => {
      $reset();
      fetchInputHomeData();
    };

    /**
     * 入力情報の新規で登録する
     * @param uploadInputFileType - アップロードするファイルの種類
     */
    const onClickHomeInputSettingData = (uploadInputFileType: UploadInputFileType) => {
      state.uploadInput.inputFileType = uploadInputFileType;
      state.mode = 'UploadInput';
    };

    /**
     * 入力情報を更新する
     * @param inputFileType - アップデートするファイルの種類
     */
    const onClickHomeInputUpdateData = async (inputFileType: InputFileType) => {
      if (!state.homeInput?.inputExcelInfo) return;
      const useData: RegisterExcelInfo = state.homeInput.inputExcelInfo[inputFileType];
      let uploadFileType: UploadInputFileType;
      switch (inputFileType) {
        case 'Inventory':
          uploadFileType = '在庫';
          break;
        case 'SalesResults':
          uploadFileType = '販売実績';
          break;
        case 'Arrival':
          uploadFileType = '入荷';
          break;
        default:
          assertNever(inputFileType);
          uploadFileType = '在庫'; // ここには来ない
          break;
      }

      // ?? より右には特に意味はないので注意
      const res = await _registerExcelInfoForm(
        uploadFileType,
        { value: useData.headerLineNumber, validateError: 'None' },
        useData.selectYearMonthPattern,
        useData.alternateHeaderInfos,
        useData.inlinedYearMonthHeaderName ?? { headerName: '', validateError: 'none' },
        useData.inHeaderYearMonthInfo ?? {
          startColumnNumber: { raw: '', validateError: 'none' },
          endColumnNumber: { raw: '', validateError: 'none' },
          start: { raw: '', validateError: 'none' },
          end: { raw: '', validateError: 'none' },
        }
      );

      if (res) {
        state.homeInput.isEdited[inputFileType] = false; // 編集関連のstateは初期化
      }
    };

    return {
      state,
      $reset,
      retry,
      fetchInputHomeData,
      onClickFirstPageNext,
      onClickUploadBusinessBack,
      onClickHomeInputBack,
      onClickUploadInputBack,
      onClickLinkingBusinessBack,
      onClickLinkingInputBack,
      onClickUploadBusinessNext,
      onClickUploadInputNext,
      onClickLinkingBusinessNext,
      onClickLinkingBusinessUsePreviousValue,
      onClickLinkingInputNext,
      onClickFinishBusinessBack,
      onClickFinishInputBack,
      dropzoneOptions2,
      getRootProps,
      getInputProps,
      isDragActive,
      handleClickDeleteFile,
      onClickHomeInputSettingData,
      onClickHomeInputUpdateData,
    };
  });

/**
 * storeの型
 */
export type RegisterSettingStore = ReturnType<ReturnType<typeof useRegisterSettingStore>>;
